// A small chat about the script: a button in the top bar opens a panel over the editor. Replies render as markdown.
// The chat is kept per episode while the app is open (it survives switching between script and timeline, not a reload).
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../api";
import { Icon } from "../common";
import { useTimeline } from "./TimelineLoader";
import "./assistant.css";

type Msg = { role: "user" | "assistant"; content: string };
const chats = new Map<string, Msg[]>();

export function AssistantButton() {
  const { ep } = useTimeline();
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const page = open ? btn.current?.closest<HTMLElement>(".page") : null; // the panel covers the whole editor page, not just the bar it sits in
  return (
    <>
      <button ref={btn} className="icon-btn as-btn" onClick={() => setOpen(true)} aria-label="Open the assistant" title="Talk about the script"><Icon name="sparkle" size={22} /></button>
      {open && page && createPortal(<AssistantPanel id={ep.id} onClose={() => setOpen(false)} />, page)}
    </>
  );
}

function AssistantPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const [msgs, setMsgs] = useState<Msg[]>(() => chats.get(id) ?? []);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { box.current?.focus(); }, []);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs, busy, error]);
  const keep = (m: Msg[]) => { chats.set(id, m); if (alive.current) setMsgs(m); };

  const send = async (history: Msg[]) => {
    setBusy(true); setError(null);
    try {
      const { reply } = await api.chat(id, history);
      keep([...history, { role: "assistant", content: reply }]);
    } catch (e) { if (alive.current) setError((e as Error).message); }
    if (alive.current) setBusy(false);
  };
  const submit = () => {
    const content = text.trim();
    if (!content || busy) return;
    const next = [...msgs, { role: "user" as const, content }];
    keep(next); setText("");
    void send(next);
  };
  const clear = () => { chats.delete(id); setMsgs([]); setError(null); };

  return (
    <div className="as-panel" role="dialog" aria-label="Assistant">
      <div className="as-head">
        <b>Assistant</b>
        <span className="as-spacer" />
        <button className="btn small" onClick={clear} disabled={busy || !msgs.length}>New chat</button>
        <button className="icon-btn subtle" onClick={onClose} aria-label="Close the assistant"><Icon name="close" size={20} /></button>
      </div>
      <div className="as-list">
        {!msgs.length && <p className="hint as-empty">Ask about the script: ideas for the next line, a funnier ending, how a scene could flow. It can read your script as it is now.</p>}
        {msgs.map((m, i) => m.role === "assistant"
          ? <div key={i} className="as-msg assistant as-md"><Markdown remarkPlugins={[remarkGfm]}>{m.content}</Markdown></div>
          : <div key={i} className="as-msg user">{m.content}</div>)}
        {busy && <div className="as-msg assistant as-thinking">thinking…</div>}
        {error && (
          <div className="as-error">{error}
            {msgs.length > 0 && msgs[msgs.length - 1]!.role === "user" && !busy && <button className="btn small" onClick={() => void send(msgs)}>Try again</button>}
          </div>
        )}
        <div ref={end} />
      </div>
      <div className="as-input">
        <textarea ref={box} rows={2} value={text} placeholder="Message" onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); } }} />
        <button className="btn small yellow" onClick={submit} disabled={busy || !text.trim()}>Send</button>
      </div>
    </div>
  );
}
